import React from 'react'

import { monthlyAmount as getMonthlyAmount, priceAmount, priceInterval } from 'libs/billing/price'

import { TaxPreview } from 'hooks/useTaxPreview'
import { getCouponTerm } from 'organisms/Billing/utils'

import { SubscriptionSummary, TaxStatus } from '.'
import { getDiscountAmount, getDiscountLabel, SummaryCoupon } from './utils'

interface SelectedPlan {
  id: string
  nickname: string
  metadata?: { saving_price?: string }
}

interface SubscriptionSummaryContainerProps {
  selectedPlan: SelectedPlan
  type: 'month' | 'year'
  coupon?: SummaryCoupon | null
  hasTax: boolean
  collectsAddress?: boolean
  isPlanChange?: boolean
  taxPreview: { isLoading: boolean; preview: TaxPreview | null }
}

export function SubscriptionSummaryContainer({
  selectedPlan,
  type,
  coupon,
  hasTax,
  collectsAddress = false,
  isPlanChange = false,
  taxPreview,
}: SubscriptionSummaryContainerProps) {
  const { isLoading, preview } = taxPreview

  const planAmount = priceAmount(selectedPlan)
  const isTaxReady = hasTax && preview?.status === 'complete'
  const discountAmount = isTaxReady
    ? (preview?.discount ?? 0) / 100
    : getDiscountAmount(planAmount, coupon)
  const total = isTaxReady ? (preview?.total ?? 0) / 100 : planAmount - discountAmount
  const discountLabel = coupon && (hasTax ? getDiscountLabel(coupon) : getCouponTerm(coupon))

  function getTaxStatus(): TaxStatus {
    if (!hasTax) {
      return 'none'
    }
    if (isLoading) {
      return 'loading'
    }
    if (isTaxReady) {
      return 'ready'
    }
    if (preview?.status === 'requires_location_inputs') {
      return collectsAddress ? 'address-empty' : 'address-missing'
    }
    return 'unavailable'
  }

  return (
    <SubscriptionSummary
      planName={selectedPlan.nickname}
      interval={priceInterval(selectedPlan) === 'year' ? 'year' : 'month'}
      planAmount={planAmount}
      monthlyAmount={getMonthlyAmount(type, planAmount)}
      savingPrice={type === 'year' ? selectedPlan.metadata?.saving_price : undefined}
      discount={
        discountLabel && discountAmount > 0
          ? { label: discountLabel, amount: discountAmount }
          : null
      }
      taxStatus={getTaxStatus()}
      taxRatePercent={preview?.tax_rate_percent ?? 0}
      taxAmount={(preview?.tax ?? 0) / 100}
      total={total}
      isPlanChange={hasTax && isPlanChange}
    />
  )
}



// WEBPACK FOOTER //
// ./src/components/SubscriptionSummary/container.tsx